import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import classNames from "classnames";
import { Tooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { firstToUpper } from "../../../utils/helpers/string";
import { renderUserName } from "../../../utils/helpers/user";

const ACTION_COLORS = {
	requested: "#1171ef",
	approved: "green",
	denied: "red",
	"request-update": "orange",
	overwritten: "orange",
	"request-deleted": "gray",
	superseded: "purple",
};

const availabilityText = (entry, t) => {
	const isAvailable = entry?.isAvailable !== false;
	if (entry?.allDay && isAvailable) {
		return t("all-day-available");
	} else if (!entry?.allDay && isAvailable) {
		return t("not-all-day-available");
	} else if (!entry?.allDay && !isAvailable) {
		return t("not-all-day-unavailable");
	}
	return t("all-day-unavailable");
};

// Stored records fill the whole day with contiguous segments (the requested windows plus
// their gaps), each flagged with `available`, so keep only the ones that were asked for.
const requestedIntervals = (entry) => {
	const isAvailable = entry?.isAvailable !== false;
	return (entry?.intervals || []).filter((interval) => (
		interval?.from
		&& interval?.to
		&& (typeof interval.available !== "boolean" || interval.available === isAvailable)
	));
};

const formatPeriod = (entry, dateFormat, t) => {
	const { startDate, endDate } = entry || {};
	if (!startDate || (endDate && moment(endDate).isSame(moment(startDate), "day"))) {
		return undefined;
	}
	return `${moment(startDate).format(dateFormat)} – ${endDate ? moment(endDate).format(dateFormat) : t("ongoing")}`;
};

function Intervals({ intervals, timeFormat }) {
	const { t } = useTranslation();

	return intervals.map((item, index) => (
		<div className="d-flex" key={index}>
			<div style={{ width: "19px" }}></div>
			<div className="d-flex align-items-center">
				<span className="mr-2 my-0 font-weight-bolder">{t("from")}:</span>
				<span>{moment.parseZone(item.from).format(timeFormat)}</span>
			</div>
			<div className="d-flex align-items-center">
				<span className="mx-2 my-0 font-weight-bolder">{firstToUpper(t("to"))}:</span>
				<span>{moment.parseZone(item.to).format(timeFormat)}</span>
			</div>
		</div>
	));
}

function Detail({ label, children }) {
	return (
		<div className="d-flex">
			<div style={{ width: "19px" }} />

			<div>
				<span className="mr-2 my-0 font-weight-bolder">
					{label}:
				</span>
				<span>{children}</span>
			</div>
		</div>
	);
}

function AvailabilityRequestView({ values }) {
	const { t } = useTranslation();
	const timeFormat = useCompanyTimeFormat();
	const dateFormat = useCompanyDateFormat();
	const { visible, toggle } = useVisible();

	const { icon, color } = useMemo(() => {
		let icon, color;
		switch (values?.status) {
			case "approved":
				icon = faCheckCircle;
				color = "text-green";
				break;
			case "pending":
				icon = faExclamationCircle;
				color = "text-warning";
				break;
			case "denied":
			case "deleted":
				icon = faXmarkCircle;
				color = "text-danger";
				break;
			default:
				break;
		};

		return { icon, color };
	}, [values?.status]);

	const intervals = useMemo(() => requestedIntervals(values?.data), [values?.data]);

	const text = useMemo(() => values?.data && availabilityText(values.data, t), [values?.data, t]);

	const period = useMemo(() => formatPeriod(values?.data, dateFormat, t), [values?.data, dateFormat, t]);

	// Newest first - older records stamp the entry with `date`, current ones with `createdAt`.
	const history = useMemo(() => {
		const entries = values?.data?.history;
		if (!Array.isArray(entries)) {
			return [];
		}
		return [...entries].sort((a, b) => (
			moment(b?.createdAt || b?.date).diff(moment(a?.createdAt || a?.date))
		));
	}, [values?.data?.history]);

	return (
		<>
			<div
				className="p-3 d-flex flex-column align-items-start justify-content-center gap-3 w-50 mb-2 w-100"
				style={{ border: "1px dashed #D5D5D1" }}
			>
				<div className="d-flex gap-3">
					<Tooltip target={`request-${values?.id}`} isOpen={visible} toggle={toggle}>
						{t(values?.status)}
					</Tooltip>
					<span id={`request-${values?.id}`}>
						<FontAwesomeIcon
							icon={icon}
							className={classNames("text-lg", color)}
						/>
					</span>
					<span>
						{text}
					</span>
				</div>

				<div className="d-flex flex-column">
					<div className="d-flex mb-1">
						<div style={{ width: "19px" }}></div>
						<span className="font-weight-bolder text-dark">
							{values?.data?.type === "recurring"
								? t(values?.data?.day)
								: moment(values?.data?.day).format(dateFormat)
							}
						</span>
					</div>

					<Intervals intervals={intervals} timeFormat={timeFormat} />
				</div>

				{period && <Detail label={t("period")}>{period}</Detail>}

				{values?.data?.reason && <Detail label={t("reason")}>{values.data.reason}</Detail>}
			</div>

			{history.length > 0 && (
				<div className="d-flex flex-column gap-2 w-100">
					<span className="font-weight-bolder text-dark">{t("history")}</span>

					{history.map((entry, index) => {
						const entryIntervals = requestedIntervals(entry);
						const entryPeriod = formatPeriod(entry, dateFormat, t);
						const date = entry?.createdAt || entry?.date;
						const note = entry?.note || entry?.comment;

						return (
							<div
								key={entry?.requestId ? `${entry.requestId}-${index}` : index}
								className="p-3 d-flex flex-column align-items-start gap-2 w-100"
								style={{ border: "1px dashed #D5D5D1" }}
							>
								<div className="d-flex justify-content-between align-items-center w-100">
									<span
										className="text-center px-3 py-1"
										style={{
											border: `1px solid ${ACTION_COLORS[entry?.action] || "gray"}`,
											color: ACTION_COLORS[entry?.action] || "gray",
											borderRadius: "20px",
										}}
									>
										{t(entry?.action)}
									</span>

									{date && (
										<span className="text-muted">
											{moment(date).format(`${dateFormat} ${timeFormat}`)}
										</span>
									)}
								</div>

								<span>{availabilityText(entry, t)}</span>

								<div className="d-flex flex-column">
									<Intervals intervals={entryIntervals} timeFormat={timeFormat} />
								</div>

								{entryPeriod && <Detail label={t("period")}>{entryPeriod}</Detail>}

								{entry?.user && <Detail label={t("by")}>{renderUserName(entry.user)}</Detail>}

								{entry?.reason && <Detail label={t("reason")}>{entry.reason}</Detail>}

								{note && <Detail label={t("note")}>{note}</Detail>}
							</div>
						);
					})}
				</div>
			)}
		</>
	);
}

export default AvailabilityRequestView;
